import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import onlineSale from "./great_online_sale.png";
import { fetchEventDetails, submitEventsData } from "../../../api/VanityLinkController";
import DiamondBackground from "../../../assets/images/backgrounds/Diamond_Checkered_Background.png";
import LoadingIcon from "../../../components/common/loading/LoadingIcon";

const EventsEmailForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const path = location.pathname;
    const pathSegments = path.split("/").filter(Boolean);
    const [eventData, setEventData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isAbove18, setIsAbove18] = useState(false);
    const [disclaimerChecked, setDisclaimerChecked] = useState(false); // optional
    const [error, setError] = useState("");
    const [count, setCount] = useState(4);
    const [formValues, setFormValues] = useState({
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        formUrl: window.location.href,
    });
    const {
        eventImage,
        eventTitle,
        formInputs,
        formButton,
        formDisclaimer,
        privacyPolicy,
        bgImage,
        bgColor,
        thankYouMessage,
    } = eventData || {};

    useEffect(() => {
        setIsLoading(true);
        setSubmitted(false);

        const fetchData = async () => {
            try {
                const fetchedEventData = await fetchEventDetails(pathSegments[1]);
                setEventData(fetchedEventData);

                // if event date is in the past, redirect to the event page because form is no longer needed
                const currentDate = new Date();
                const eventDate = new Date(fetchedEventData?.eventStartDate);
                if (
                    fetchedEventData?.eventStartDate !== null &&
                    fetchedEventData?.eventStartDate !== undefined &&
                    eventDate <= currentDate
                ) {
                    navigate(`/${fetchedEventData.vanityLinkName}`);
                }
                if (fetchedEventData === null || fetchedEventData === undefined) {
                    // fetchData();
                    // navigate("/");
                }
            } catch (error) {
                console.error("Error fetching event data:", error);
            } finally {
                setIsLoading(false);
                setShowForm(true);
            }
        };
        if (pathSegments[1]) {
            fetchData();
        }
    }, [location]);

    const renderTitleWithSpan = (text) => {
        if (!text) return null;

        const parts = text.split(/<span>|<\/span>/);
        return parts.map((part, index) => (index % 2 === 1 ? <span key={index}>{part}</span> : part));
    };

    useEffect(() => {
        let interval;

        if (submitted) {
            interval = setInterval(() => {
                setCount((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        navigate(`/${eventData.vanityLinkName}`, { replace: true });
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [submitted]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Find the matching field config, or fall back to undefined
        const field = formInputs?.find((f) => f.key === name);

        // If no field config, just update the value as-is
        if (!field) {
            setFormValues((prev) => ({
                ...prev,
                [name]: value,
            }));
            return;
        }

        const prefix = field.prefix || "";
        const pattern = field.pattern || "";

        // Only handle specially if a pattern is defined (e.g., phone number)
        if (pattern) {
            // Extract max digits from the pattern (e.g. [0-9]{8})
            const match = pattern.match(/\[0-9\]{(\d+)}/);
            const maxDigits = match ? parseInt(match[1], 10) : null;

            if (value === "") {
                setFormValues((prev) => ({
                    ...prev,
                    [name]: "",
                }));
                return;
            }

            // If there's a prefix, prevent editing it
            if (prefix && !value.startsWith(prefix)) {
                return;
            }

            // Get content after the prefix and keep only digits
            const afterPrefix = value.slice(prefix.length).replace(/\D/g, "");

            // Limit to maxDigits if defined
            const finalDigits = maxDigits ? afterPrefix.slice(0, maxDigits) : afterPrefix;

            setFormValues((prev) => ({
                ...prev,
                [name]: prefix + finalDigits,
            }));
            return;
        }

        // Default handling for all other fields
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!isAbove18) {
            setError(
                "You must confirm that you are above 18 years of age and consent to the processing of your personal data."
            );
            return;
        }
        const updatedFormValues = { ...formValues, isAbove18, disclaimerChecked };

        setError(""); // clear error if above validation passes
        setIsLoading(true);

        const resp = await submitEventsData(updatedFormValues);

        if (resp.status === 200) {
            setSubmitted(true);
            setIsLoading(false);
        } else {
            console.log("Error submitting form");
            setSubmitted(false);
            setIsLoading(false);
        }
    };

    if (isLoading || !showForm) {
        return (
            <EventBackground bgColor={"#f2f2f2"}>
                <LoadingIcon color="black" />
            </EventBackground>
        );
    }

    return (
        <>
            <EventBackground bgImage={bgImage} bgColor={bgColor}>
                <EventFormContainer>
                    <EventLeftImage src={eventImage || onlineSale} alt="Form Visual" />
                    {submitted ? (
                        <EventTerms>
                            <ThankYouNote dangerouslySetInnerHTML={{ __html: thankYouMessage }} />
                            <ThankYouNote style={{ marginTop: "20px" }}>You will be redirected in {count}</ThankYouNote>
                        </EventTerms>
                    ) : (
                        <EventFormFields onSubmit={handleFormSubmit}>
                            <EventTitle>{renderTitleWithSpan(eventTitle)}</EventTitle>
                            {formInputs?.map((field, index) => (
                                <InputContainer key={index}>
                                    <EventInput
                                        required={field.required}
                                        type={field.type}
                                        name={field.key}
                                        placeholder={field.placeholder}
                                        value={formValues[field.key] || ""}
                                        onChange={handleInputChange}
                                        pattern={field.pattern || undefined}
                                        inputMode={field.key === "mobileNumber" ? "numeric" : undefined}
                                        maxLength={field.key === "mobileNumber" ? 15 : undefined}
                                        onFocus={(e) => {
                                            const fieldName = e.target.name;
                                            const fieldConfig = formInputs?.find((f) => f.key === fieldName);

                                            if (!fieldConfig) return; // No config, do nothing

                                            const prefix = fieldConfig.prefix || "";

                                            if (fieldName === "mobileNumber" && !e.target.value.startsWith(prefix)) {
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    [fieldName]: prefix,
                                                }));
                                            }
                                        }}
                                    />
                                    {field.required ? <span> *</span> : <span style={{ marginLeft: "12px" }} />}
                                </InputContainer>
                            ))}

                            <CheckboxContainer>
                                <InputContainer>
                                    <input
                                        type="checkbox"
                                        checked={disclaimerChecked}
                                        onChange={(e) => setDisclaimerChecked(e.target.checked)}
                                        style={{ marginRight: "5px" }}
                                    />
                                    <Text dangerouslySetInnerHTML={{ __html: formDisclaimer }} />
                                </InputContainer>
                            </CheckboxContainer>

                            <CheckboxContainer>
                                <InputContainer>
                                    <input
                                        type="checkbox"
                                        checked={isAbove18}
                                        onChange={(e) => setIsAbove18(e.target.checked)}
                                        style={{ marginRight: "5px" }}
                                    />

                                    <Text dangerouslySetInnerHTML={{ __html: privacyPolicy }} />
                                </InputContainer>
                            </CheckboxContainer>

                            {error && <ErrorMessage>{error}</ErrorMessage>}

                            <FormButton ageConsent={!isAbove18}>{formButton}</FormButton>
                            <ThankYouNote>
                                Already subscribed?{" "}
                                <Link to={`/${eventData.vanityLinkName}`} replace>
                                    Enter here
                                </Link>{" "}
                                to shop now, or go to our traditional shopping experience{" "}
                                <a
                                    href="https://www.visitdubai.com/en/deals-and-offers/great-online-sale-2025"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    here
                                </a>
                            </ThankYouNote>
                        </EventFormFields>
                    )}
                </EventFormContainer>
            </EventBackground>
        </>
    );
};

export default EventsEmailForm;

const EventBackground = styled.div`
    width: 100vw;
    min-height: 100vh;
    background: ${({ bgImage, bgColor }) => (bgImage ? `url(${bgImage})` : bgColor || `url(${DiamondBackground})`)};
    background-size: ${({ bgImage, bgColor }) => (!bgImage && !bgColor ? `350px auto` : `cover`)};
    background-position: center;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Text = styled.p`
    font-size: 12px;
    color: #333;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
`;
const EventFormContainer = styled.div`
    @import url("@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');");
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 30px 30px 30px;
    margin-top: 0rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-width: 700px;
    width: 100%;

    @media (min-width: 768px) {
        flex-direction: row;
        justify-content: flex-start;
        padding: 30px;
        margin-top: 2rem;
    }
`;

const EventLeftImage = styled.img`
    display: flex;
    justify-content: flex-start;
    width: 80%;
    height: 80%;
    max-width: 300px;
    margin-bottom: -20px;

    @media (min-width: 768px) {
        margin-bottom: 0;
        margin-right: 20px;
        width: 100%;
        height: 100%;
    }
`;

const EventFormFields = styled.form`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
`;

const FormButton = styled.button`
    padding: 10px;
    width: 160px;
    border-radius: 4px;
    color: white;
    background: ${({ ageConsent }) => (ageConsent ? "grey" : "#355c81")};
    border: none;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;

    @media (max-width: 768px) {
        align-self: center;
    }
`;

const EventInput = styled.input`
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100%;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
`;
const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    & span {
        color: red;
        font-size: 20px;
        margin-left: 5px;
    }
`;

const EventTitle = styled.h2`
    margin-bottom: 20px;
    font-size: 28px;
    font-family: "Montserrat", sans-serif;
    font-weight: 900;
    line-height: 1.2;
    letter-spacing: normal;

    & span {
        color: #951418;
        font-size: 26px;
        font-weight: 900;
    }

    @media (max-width: 768px) {
        margin-bottom: 5px;
        text-align: center;
    }
`;

const EventTerms = styled.p`
    margin-top: 20px;
    font-size: 12px;
    color: #666;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
    padding-right: 1rem;

    @media (max-width: 768px) {
        text-align: center;
    }
`;
const ThankYouNote = styled.p`
    margin-top: 0px;
    font-size: 14px;
    color: #666;
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
    padding-right: 1rem;

    @media (max-width: 768px) {
        text-align: center;
    }
`;
const EventDisclaimer = styled.p`
    font-family: "Montserrat", sans-serif;
    letter-spacing: normal;
    margin-bottom: 1rem;
    margin-top: 0rem;
    font-size: 12px;
    color: #666;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 12px;
    color: #333;
    margin-bottom: 10px;

    & label {
        font-family: "Montserrat", sans-serif;
        letter-spacing: normal;
    }
`;

const ErrorMessage = styled.p`
    color: red;
    font-size: 12px;
    margin-bottom: 10px;
`;
